import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";

interface ExtractedBOMItem {
  id: string;
  markNumber: string;
  productName: string;
  quantity: number;
  dimensions: {
    length: number;
    width: number;
    thickness: number;
  };
  weight: number;
  material: string;
  validated: boolean;
  productId?: number;
}

interface EngineeringData {
  projectId: number | null;
  cadFiles: File[];
  fileType: "revit" | "autocad" | "ifc" | "pdf";
  extractedItems: ExtractedBOMItem[];
  mappingRules: {
    autoMatch: boolean;
    createNewProducts: boolean;
    updateExisting: boolean;
  };
}

class EngineeringDataWizardStore {
  rootStore: RootStore;

  // Observables
  activeStep = 0;
  loading = false;
  processing = false;
  error: string | null = null;
  success: string | null = null;
  uploadProgress = 0;

  engineeringData: EngineeringData = {
    projectId: null,
    cadFiles: [],
    fileType: "revit",
    extractedItems: [],
    mappingRules: {
      autoMatch: true,
      createNewProducts: false,
      updateExisting: true,
    },
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    this.activeStep--;
  };

  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setProcessing = (processing: boolean) => {
    this.processing = processing;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  setUploadProgress = (progress: number) => {
    this.uploadProgress = progress;
  };

  updateEngineeringData = <K extends keyof EngineeringData>(
    field: K,
    value: EngineeringData[K]
  ) => {
    this.engineeringData[field] = value;
  };

  updateMappingRules = <K extends keyof EngineeringData["mappingRules"]>(
    field: K,
    value: EngineeringData["mappingRules"][K]
  ) => {
    this.engineeringData.mappingRules[field] = value;
  };

  addCADFiles = (files: FileList) => {
    const newFiles = Array.from(files);
    this.engineeringData.cadFiles = [...this.engineeringData.cadFiles, ...newFiles];
  };

  removeCADFile = (index: number) => {
    this.engineeringData.cadFiles = this.engineeringData.cadFiles.filter((_, i) => i !== index);
  };

  validateItem = (itemId: string) => {
    this.engineeringData.extractedItems = this.engineeringData.extractedItems.map((item) =>
      item.id === itemId ? { ...item, validated: !item.validated } : item
    );
  };

  updateItem = (itemId: string, field: string, value: any) => {
    this.engineeringData.extractedItems = this.engineeringData.extractedItems.map((item) =>
      item.id === itemId ? { ...item, [field]: value } : item
    );
  };

  // Validation
  validateCurrentStep = (): boolean => {
    this.setError(null);

    switch (this.activeStep) {
      case 0: // Project & File Upload
        if (!this.engineeringData.projectId) {
          this.setError("Please select a project");
          return false;
        }
        if (this.engineeringData.cadFiles.length === 0) {
          this.setError("Please upload at least one CAD file");
          return false;
        }
        break;

      case 1: // Data Extraction
        // Extraction happens automatically, just check if processing
        if (this.processing) {
          this.setError("Data extraction is still in progress");
          return false;
        }
        if (this.engineeringData.extractedItems.length === 0) {
          this.setError("No items were extracted from the files");
          return false;
        }
        break;

      case 2: // Data Validation
        if (this.validatedItems.length === 0) {
          this.setError("Please validate at least one item before proceeding");
          return false;
        }
        break;

      case 3: // Mapping Rules
        // Optional step - validation not strictly required
        break;
    }

    return true;
  };

  // Async actions
  loadInitialData = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const jobStore = this.rootStore.jobStore as ServiceStore;
      const billOfMaterialsStore = this.rootStore.billOfMaterialsStore as ServiceStore;
      const productsStore = this.rootStore.productsStore as ServiceStore;

      await Promise.all([
        jobStore?.find({ query: { archived: false } }),
        billOfMaterialsStore?.find(),
        productsStore?.find(),
      ]);
    } catch (err: any) {
      runInAction(() => {
        this.setError("Failed to load initial data");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  extractData = async () => {
    this.setProcessing(true);
    this.setError(null);
    this.setUploadProgress(0);

    try {
      // Simulate file upload and extraction process
      // In real implementation, this would upload to server and process
      for (let i = 0; i <= 100; i += 10) {
        runInAction(() => {
          this.setUploadProgress(i);
        });
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      const bomExtractionStore = this.rootStore.bomExtractionStore as ServiceStore;

      // Create extraction record
      const extraction = await bomExtractionStore?.create({
        jobId: this.engineeringData.projectId,
        fileName: this.engineeringData.cadFiles[0]?.name,
        fileType: this.engineeringData.fileType,
        status: "processing",
        extractedAt: new Date().toISOString(),
      });

      // Simulate extracted data (in production, this would come from the server)
      const simulatedItems: ExtractedBOMItem[] = [
        {
          id: "1",
          markNumber: "W1",
          productName: "Wall Panel Type A",
          quantity: 12,
          dimensions: { length: 20, width: 10, thickness: 6 },
          weight: 8500,
          material: "Concrete",
          validated: false,
        },
        {
          id: "2",
          markNumber: "W2",
          productName: "Wall Panel Type B",
          quantity: 8,
          dimensions: { length: 15, width: 10, thickness: 6 },
          weight: 6500,
          material: "Concrete",
          validated: false,
        },
        {
          id: "3",
          markNumber: "S1",
          productName: "Slab Panel",
          quantity: 20,
          dimensions: { length: 30, width: 8, thickness: 8 },
          weight: 12000,
          material: "Concrete",
          validated: false,
        },
        {
          id: "4",
          markNumber: "B1",
          productName: "Beam Element",
          quantity: 15,
          dimensions: { length: 25, width: 2, thickness: 3 },
          weight: 3500,
          material: "Prestressed Concrete",
          validated: false,
        },
      ];

      runInAction(() => {
        this.engineeringData.extractedItems = simulatedItems;
        this.setSuccess("Data extracted successfully!");
        this.nextStep();
      });
    } catch (err: any) {
      runInAction(() => {
        this.setError(err.message || "Failed to extract data");
      });
    } finally {
      runInAction(() => {
        this.setProcessing(false);
        this.setUploadProgress(0);
      });
    }
  };

  createBOM = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const jobStore = this.rootStore.jobStore as ServiceStore;
      const billOfMaterialsStore = this.rootStore.billOfMaterialsStore as ServiceStore;
      const bomItemsStore = this.rootStore.bomItemsStore as ServiceStore;
      const productsStore = this.rootStore.productsStore as ServiceStore;

      const job = jobStore?.entities.find((j: any) => j.id === this.engineeringData.projectId);

      // Create BOM
      const bomResult = await billOfMaterialsStore?.create({
        bomCode: `BOM-${job?.jobNumber}-${Date.now()}`,
        name: `${job?.name} - Bill of Materials`,
        description: "Auto-generated from CAD files",
        version: "1.0",
        status: "draft",
        active: true,
        effectiveDate: new Date().toISOString(),
      });
      const bom = (Array.isArray(bomResult) ? bomResult[0] : bomResult) as
        | { id: string | number }
        | undefined;

      if (!bom) {
        throw new Error("Failed to create BOM");
      }

      // Create or link products and BOM items
      for (const item of this.validatedItems) {
        let product = productsStore?.entities.find(
          (p: any) => p.name === item.productName
        );

        // Create new product if needed
        if (!product && this.engineeringData.mappingRules.createNewProducts) {
          const createdProduct = await productsStore?.create({
            name: item.productName,
            markNumber: item.markNumber,
            length: item.dimensions.length,
            width: item.dimensions.width,
            thickness: item.dimensions.thickness,
            weight: item.weight,
            material: item.material,
            category: "Precast",
            status: "active",
          });
          product = (Array.isArray(createdProduct)
            ? createdProduct[0]
            : createdProduct) as typeof product;
        }

        if (product) {
          // Create BOM item
          await bomItemsStore?.create({
            bomId: bom.id,
            productId: product.id,
            quantity: item.quantity,
            unitCost: 0, // To be filled later
            totalCost: 0,
            sequence: parseInt(item.id),
            notes: `Mark: ${item.markNumber}`,
          });
        }
      }

      runInAction(() => {
        this.setSuccess("Bill of Materials created successfully!");
        this.nextStep();
      });
    } catch (err: any) {
      runInAction(() => {
        this.setError(err.message || "Failed to create BOM");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.processing = false;
    this.error = null;
    this.success = null;
    this.uploadProgress = 0;
    this.engineeringData = {
      projectId: null,
      cadFiles: [],
      fileType: "revit",
      extractedItems: [],
      mappingRules: {
        autoMatch: true,
        createNewProducts: false,
        updateExisting: true,
      },
    };
  };

  // Computed values
  get jobs() {
    const jobStore = this.rootStore.jobStore as ServiceStore;
    return jobStore?.entities || [];
  }

  get products() {
    const productsStore = this.rootStore.productsStore as ServiceStore;
    return productsStore?.entities || [];
  }

  get validatedItems() {
    return this.engineeringData.extractedItems.filter((item) => item.validated);
  }

  get totalValidatedProducts() {
    return this.validatedItems.length;
  }

  get totalValidatedQuantity() {
    return this.validatedItems.reduce((sum, item) => sum + item.quantity, 0);
  }

  get totalValidatedWeight() {
    return this.validatedItems.reduce((sum, item) => sum + item.weight * item.quantity, 0);
  }

  get canProceedFromUpload() {
    return !!this.engineeringData.projectId && this.engineeringData.cadFiles.length > 0 && !this.processing;
  }

  get canProceedFromValidation() {
    return this.validatedItems.length > 0;
  }
}

export default EngineeringDataWizardStore;